<template>
    <Page>
        <Container class="max-w-screen-2xl">
            <div class="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <Title class="!mb-1">Product Inbox</Title>
                    <div class="text-sm text-neutral-500 dark:text-neutral-400">
                        Review feature ideas, bug reports, and feedback without touching survey responses.
                    </div>
                </div>
                <button
                    class="rounded border border-neutral-300 px-3 py-2 text-center text-sm font-semibold text-neutral-900 dark:border-neutral-700 dark:text-white"
                    type="button"
                    @click="loadItems"
                >
                    <Icon icon="rotate" fixed-width/>
                    Refresh
                </button>
            </div>

            <div class="mb-5 flex flex-wrap gap-2">
                <button
                    v-for="board in boardTypes"
                    :key="board.key"
                    class="rounded border px-3 py-2 text-center text-sm font-semibold"
                    :class="activeType === board.key
                        ? 'border-brand-primary-600 bg-brand-primary-600 text-white'
                        : 'border-neutral-300 text-neutral-700 dark:border-neutral-700 dark:text-neutral-200'"
                    type="button"
                    @click="activeType = board.key"
                >
                    {{ board.label }}
                    <span class="ml-1 text-xs opacity-75">{{ typeCount(board.key) }}</span>
                </button>
            </div>

            <Loading v-if="loading" />

            <div v-else-if="activeBoardItems.length === 0" class="rounded bg-neutral-100 p-4 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                No {{ activeBoard.emptyLabel }} have been submitted yet.
            </div>

            <div v-else class="grid gap-4 lg:grid-cols-3">
                <section
                    v-for="column in boardColumns"
                    :key="column.key"
                    class="min-w-0 rounded border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-950"
                >
                    <div class="mb-3 flex items-center justify-between gap-3">
                        <div class="text-sm font-semibold uppercase text-neutral-600 dark:text-neutral-300">{{ column.label }}</div>
                        <div class="rounded bg-neutral-200 px-2 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                            {{ itemsForColumn(column).length }}
                        </div>
                    </div>

                    <draggable
                        :model-value="itemsForColumn(column)"
                        :group="dragGroup"
                        item-key="id"
                        class="min-h-[4rem] space-y-2"
                        ghost-class="ticket-card-ghost"
                        chosen-class="ticket-card-chosen"
                        @change="handleColumnChange(column, $event)"
                    >
                        <template #item="{element: item}">
                        <button
                            :key="item.id"
                            class="ticket-card block w-full rounded border border-neutral-200 bg-white p-3 text-left shadow-sm transition hover:border-brand-primary-400 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-brand-primary-500 dark:hover:bg-neutral-800"
                            type="button"
                            @click="openItem(item)"
                        >
                            <div class="ticket-card-title text-base font-semibold leading-6 text-neutral-950 dark:text-white">
                                {{ cardTitle(item) }}
                            </div>

                            <div class="ticket-card-description mt-1 text-[11px] leading-4 text-neutral-500 dark:text-neutral-400">
                                {{ item.description }}
                            </div>

                            <div class="mt-3 flex flex-wrap items-center gap-1.5 text-[10px] text-neutral-500 dark:text-neutral-400">
                                <span class="inline-flex items-center rounded border px-1.5 py-0.5 text-[8px] font-semibold leading-none" :class="cardTypeClass(item.type)">
                                    {{ cardTypeLabel(item.type) }}
                                </span>
                                <span v-if="voteSummary(item.metadata || {})" class="inline-flex items-center gap-1 font-medium text-neutral-600 dark:text-neutral-300">
                                    {{ voteSummary(item.metadata || {}) }}
                                </span>
                                <span v-if="(item.metadata || {}).prompt_location">{{ (item.metadata || {}).prompt_location }}</span>
                            </div>
                        </button>
                        </template>
                    </draggable>

                    <div v-if="itemsForColumn(column).length === 0" class="mt-2 rounded border border-dashed border-neutral-300 p-3 text-center text-xs text-neutral-500 dark:border-neutral-800 dark:text-neutral-500">
                        Drop {{ activeBoard.cardLabel }} here
                    </div>
                </section>
            </div>
        </Container>

        <div
            v-if="selectedItem"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
            @click.self="closeModal"
        >
            <section class="flex max-h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded border border-neutral-800 bg-white text-neutral-950 shadow-2xl dark:bg-neutral-950 dark:text-white md:max-h-[82vh]">
                <div class="flex items-start justify-between gap-4 border-b border-neutral-200 p-4 dark:border-neutral-800">
                    <div class="min-w-0">
                        <div class="mb-2 flex flex-wrap items-center gap-2">
                            <span class="rounded px-2 py-1 text-xs font-semibold" :class="typeClass(selectedItem.type)">
                                {{ typeLabel(selectedItem.type) }}
                            </span>
                            <span class="rounded bg-neutral-200 px-2 py-1 text-xs font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                                {{ statusLabel(selectedItem.status, selectedItem.type) }}
                            </span>
                            <span class="text-xs text-neutral-500 dark:text-neutral-400">
                                #{{ selectedItem.id }}
                            </span>
                        </div>
                        <div class="text-xl font-semibold leading-7">
                            {{ cardTitle(selectedItem) }}
                        </div>
                    </div>
                    <button
                        class="rounded border border-neutral-300 px-3 py-2 text-neutral-700 dark:border-neutral-700 dark:text-neutral-200"
                        type="button"
                        @click="closeModal"
                    >
                        <Icon icon="times" fixed-width/>
                    </button>
                </div>

                <div class="flex-1 overflow-y-auto p-4">
                    <div class="feedback-modal-layout">
                        <div class="feedback-modal-editor space-y-4">
                            <div>
                                <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                    Title
                                </label>
                                <input
                                    v-model="modalDraft.title"
                                    class="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
                                    :disabled="updatingId === selectedItem.id"
                                />
                            </div>

                            <div>
                                <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                    Description
                                </label>
                                <textarea
                                    v-model="modalDraft.description"
                                    class="min-h-[180px] w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm leading-6 text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
                                    :disabled="updatingId === selectedItem.id"
                                ></textarea>
                            </div>

                            <div class="grid gap-3 sm:grid-cols-2" :class="isFeatureIdea(selectedItem) ? 'xl:grid-cols-3' : null">
                                <div>
                                    <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                        Type
                                    </label>
                                    <select
                                        v-model="modalDraft.type"
                                        class="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
                                        :disabled="updatingId === selectedItem.id || isFeatureIdea(selectedItem)"
                                    >
                                        <option v-for="type in types" :key="type.key" :value="type.key">
                                            {{ type.label }}
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                        Status
                                    </label>
                                    <select
                                        v-model="modalDraft.status"
                                        class="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
                                    :disabled="updatingId === selectedItem.id"
                                >
                                    <option v-for="status in statusOptionsFor(selectedItem.type)" :key="status.key" :value="status.key">
                                        {{ status.label }}
                                    </option>
                                </select>
                                </div>

                                <div v-if="isFeatureIdea(selectedItem)">
                                    <label class="mb-1 block text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                                        Category
                                    </label>
                                    <select
                                        v-model="modalDraft.category"
                                        class="w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
                                        :disabled="updatingId === selectedItem.id"
                                    >
                                        <option value="">Uncategorized</option>
                                        <option v-for="category in categories" :key="category.key" :value="category.key">
                                            {{ category.label }}
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <div v-if="errors[selectedItem.id]" class="rounded border border-red-800 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-100">
                                {{ errors[selectedItem.id] }}
                            </div>
                        </div>

                        <aside class="feedback-modal-meta space-y-3">
                            <section v-if="isFeatureIdea(selectedItem)" class="rounded border border-neutral-200 bg-neutral-50 px-3 py-3 dark:border-neutral-800 dark:bg-neutral-900">
                                <div class="mb-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                                    Idea Signals
                                </div>
                                <dl class="space-y-2 text-xs">
                                    <div>
                                        <dt class="text-neutral-500 dark:text-neutral-400">Voting</dt>
                                        <dd class="mt-0.5 font-medium text-neutral-900 dark:text-white">{{ voteDetailSummary(selectedItem.metadata || {}) }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-neutral-500 dark:text-neutral-400">Visibility</dt>
                                        <dd class="mt-0.5 font-medium text-neutral-900 dark:text-white">{{ ideaVisibilityLabel(modalDraft.status) }}</dd>
                                    </div>
                                </dl>
                            </section>

                            <section class="rounded border border-neutral-200 bg-neutral-50 px-3 py-3 dark:border-neutral-800 dark:bg-neutral-900">
                                <div class="mb-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                                    Ticket Details
                                </div>
                                <dl class="space-y-2 text-xs">
                                    <div v-for="entry in ticketDetailEntries(selectedItem)" :key="entry.label">
                                        <dt class="text-neutral-500 dark:text-neutral-400">{{ entry.label }}</dt>
                                        <dd class="mt-0.5 break-words font-medium text-neutral-900 dark:text-white">{{ entry.value }}</dd>
                                    </div>
                                </dl>
                            </section>

                            <section v-if="contextEntries(selectedItem).length" class="rounded border border-neutral-200 bg-neutral-50 px-3 py-3 dark:border-neutral-800 dark:bg-neutral-900">
                                <div class="mb-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                                    Context
                                </div>
                                <dl class="space-y-2 text-xs">
                                    <div v-for="entry in contextEntries(selectedItem)" :key="entry.label">
                                        <dt class="text-neutral-500 dark:text-neutral-400">{{ entry.label }}</dt>
                                        <dd class="mt-0.5 break-words font-medium text-neutral-900 dark:text-white">{{ entry.value }}</dd>
                                    </div>
                                </dl>
                            </section>

                            <section v-if="technicalEntries(selectedItem).length" class="rounded border border-neutral-200 bg-neutral-50 px-3 py-3 dark:border-neutral-800 dark:bg-neutral-900">
                                <div class="mb-2 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                                    Technical
                                </div>
                                <dl class="space-y-2 text-xs">
                                    <div v-for="entry in technicalEntries(selectedItem)" :key="entry.label">
                                        <dt class="text-neutral-500 dark:text-neutral-400">{{ entry.label }}</dt>
                                        <dd class="mt-0.5 break-words font-medium text-neutral-900 dark:text-white" :class="entry.pre ? 'whitespace-pre-wrap' : null">
                                            <a
                                                v-if="entry.url"
                                                :href="entry.value"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="text-brand-primary-700 underline decoration-brand-primary-700/30 dark:text-brand-primary-300"
                                            >
                                                {{ entry.value }}
                                            </a>
                                            <span v-else>{{ entry.value }}</span>
                                        </dd>
                                    </div>
                                </dl>
                            </section>
                        </aside>
                    </div>
                </div>

                <div class="flex flex-col-reverse gap-3 border-t border-neutral-200 p-4 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
                    <button
                        class="rounded border border-red-800 px-3 py-2 text-center text-sm font-semibold text-red-800"
                        type="button"
                        :disabled="deletingId === selectedItem.id"
                        @click="confirmDelete(selectedItem)"
                    >
                        <Icon icon="trash-alt" fixed-width/>
                        Delete
                    </button>

                    <div class="flex items-center justify-end gap-3">
                        <button
                            class="rounded border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 dark:border-neutral-700 dark:text-neutral-200"
                            type="button"
                            @click="closeModal"
                        >
                            Cancel
                        </button>
                        <button
                            class="rounded border border-brand-primary-600 bg-brand-primary-600 px-4 py-2 text-sm font-semibold text-white"
                            type="button"
                            :disabled="updatingId === selectedItem.id"
                            @click="saveSelectedItem"
                        >
                            {{ updatingId === selectedItem.id ? 'Saving...' : 'Save' }}
                        </button>
                    </div>
                </div>
            </section>
        </div>
    </Page>
</template>

<script>
import {defineComponent} from 'vue';
import {mapActions} from 'vuex';
import moment from 'moment-timezone';
import draggable from 'vuedraggable';

const statuses = [
  {key: 'open', label: 'Open'},
  {key: 'in_progress', label: 'In Progress'},
  {key: 'done', label: 'Done'},
];

const types = [
  {key: 'feature_idea', label: 'Feature Idea'},
  {key: 'bug', label: 'Bug'},
  {key: 'feedback', label: 'Feedback'},
];

const categories = [
  {key: 'workout_logging', label: 'Workout Logging'},
  {key: 'analytics', label: 'Analytics'},
  {key: 'progress_photos', label: 'Progress Photos'},
  {key: 'templates', label: 'Templates'},
  {key: 'integrations', label: 'Integrations'},
  {key: 'coaching', label: 'Coaching'},
  {key: 'other', label: 'Other'},
];

const boardConfigs = {
  feature_idea: {
    cardLabel: 'ideas',
    emptyLabel: 'feature ideas',
    label: 'Feature Ideas',
    statuses: [
      {key: 'open', label: 'Open'},
      {key: 'in_progress', label: 'Voting'},
      {key: 'done', label: 'Done'},
    ],
  },
  bug: {
    cardLabel: 'bugs',
    emptyLabel: 'bug reports',
    label: 'Bugs',
    statuses: [
      {key: 'open', label: 'Open'},
      {key: 'in_progress', label: 'Investigating'},
      {key: 'done', label: 'Resolved'},
    ],
  },
  feedback: {
    cardLabel: 'feedback',
    emptyLabel: 'feedback',
    label: 'Feedback',
    statuses: [
      {key: 'open', label: 'New'},
      {key: 'in_progress', label: 'Reviewing'},
      {key: 'done', label: 'Done'},
    ],
  },
};

export default defineComponent({
  name: 'ProductFeedback',
  components: {
    draggable,
  },
  created() {
    this.loadItems();
  },
  data() {
    return {
      activeType: 'feature_idea',
      categories,
      deletingId: null,
      errors: {},
      items: [],
      loading: false,
      modalDraft: {},
      selectedItemId: null,
      statuses,
      types,
      updatingId: null,
    };
  },
  computed: {
    activeBoard() {
      return boardConfigs[this.activeType] || boardConfigs.feature_idea;
    },
    activeBoardItems() {
      return this.items.filter(item => item.type === this.activeType);
    },
    boardColumns() {
      return this.activeBoard.statuses;
    },
    boardTypes() {
      return types.map(type => ({
        ...type,
        label: boardConfigs[type.key]?.label || type.label,
      }));
    },
    dragGroup() {
      return `product-feedback-${this.activeType}`;
    },
    selectedItem() {
      if (!this.selectedItemId) {
        return null;
      }

      return this.items.find(item => item.id === this.selectedItemId) || null;
    },
  },
  methods: {
    ...mapActions({
      adminDeleteProductFeedback: 'admin/deleteProductFeedback',
      adminProductFeedback: 'admin/productFeedback',
      adminUpdateFeatureIdea: 'admin/updateFeatureIdea',
      adminUpdateProductFeedback: 'admin/updateProductFeedback',
    }),
    addMeta(entries, label, value, url = false, pre = false) {
      if (value === null || value === undefined || value === '') {
        return;
      }

      entries.push({
        label,
        pre,
        url,
        value,
      });
    },
    cardTitle(item) {
      return item.title || item.description?.slice(0, 90) || `Feedback #${item.id}`;
    },
    categoryLabel(category) {
      if (!category) {
        return null;
      }

      return categories.find(item => item.key === category)?.label || this.humanizeMetaLabel(category);
    },
    cardTypeClass(type) {
      if (type === 'bug') {
        return 'border-red-900 bg-red-950 text-red-200';
      }

      return 'border-brand-primary-600 bg-brand-primary-600 text-white';
    },
    cardTypeLabel(type) {
      if (type === 'feature_idea') {
        return 'Idea';
      }

      return this.typeLabel(type);
    },
    closeModal() {
      this.selectedItemId = null;
      this.modalDraft = {};
    },
    confirmDelete(item) {
      window.app.dialog({
        title: 'Delete Feedback Item',
        message: 'Delete this inbox item? The original legacy source record will be preserved.',
        actions: [
          {
            text: 'Cancel',
            outlined: true,
            click: () => {},
          },
          {
            text: 'Delete',
            danger: true,
            click: () => this.deleteItem(item),
          },
        ],
      });
    },
    creatorName(item) {
      if (!item.user) {
        return 'Unknown creator';
      }

      return [item.user.first_name, item.user.last_name].filter(Boolean).join(' ') || item.user.email;
    },
    creatorSummary(item) {
      if (!item.user) {
        return `Unknown${item.user_id ? ` (#${item.user_id})` : ''}`;
      }

      const name = [item.user.first_name, item.user.last_name].filter(Boolean).join(' ');

      return name ? `${name} <${item.user.email}>` : item.user.email;
    },
    deleteItem(item) {
      this.deletingId = item.id;

      return this.adminDeleteProductFeedback({id: item.id}).then(() => {
        this.items = this.items.filter(candidate => candidate.id !== item.id);
        this.closeModal();
        window.app.snackbar('Feedback item deleted');
      }).catch((error) => {
        window.app.snackbarError(error);
      }).finally(() => {
        this.deletingId = null;
      });
    },
    formatDate(dateString) {
      if (!dateString) {
        return '';
      }

      return moment(dateString).fromNow();
    },
    feedbackTypeLabel(feedbackType) {
      if (feedbackType === 'bug_report') {
        return 'Bug Report';
      }

      if (feedbackType === 'feature_feedback') {
        return 'Feature Feedback';
      }

      if (feedbackType === 'general_feedback') {
        return 'General Feedback';
      }

      return this.humanizeMetaLabel(feedbackType);
    },
    humanizeMetaLabel(key) {
      return String(key || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, character => character.toUpperCase());
    },
    handleColumnChange(column, event) {
      if (!event?.added?.element) {
        return;
      }

      this.moveItemToColumn(event.added.element, column);
    },
    ideaVisibilityLabel(status) {
      if (status === 'in_progress') {
        return 'Visible for voting';
      }

      if (status === 'done') {
        return 'Closed';
      }

      return 'Internal review';
    },
    isFeatureIdea(item) {
      return item?.source_type === 'feature_ideas';
    },
    itemsForColumn(column) {
      return this.activeBoardItems.filter(item => item.status === column.key);
    },
    loadItems() {
      this.loading = true;

      return this.adminProductFeedback().then((response) => {
        this.items = response.data.data || [];
      }).catch((error) => {
        window.app.snackbarError(error);
      }).finally(() => {
        this.loading = false;
      });
    },
    contextEntries(item) {
      const metadata = item.metadata || {};
      const legacyMetadata = metadata.legacy_metadata || {};
      const entries = [];

      this.addMeta(entries, 'Prompt', metadata.prompt_location);
      this.addMeta(entries, 'Category', this.categoryLabel(metadata.category));
      this.addMeta(entries, 'Feedback Type', this.feedbackTypeLabel(metadata.feedback_type));
      this.addMeta(entries, 'Votes', this.voteSummary(metadata));

      Object.entries(legacyMetadata).forEach(([key, value]) => {
        this.addMeta(entries, this.humanizeMetaLabel(key), this.metaValue(value));
      });

      return entries;
    },
    openItem(item) {
      this.selectedItemId = item.id;
      this.modalDraft = {
        category: item.metadata?.category || '',
        description: item.description || '',
        status: item.status,
        title: item.title || '',
        type: item.type,
      };
    },
    saveSelectedItem() {
      if (!this.selectedItem) {
        return;
      }

      if (this.isFeatureIdea(this.selectedItem)) {
        return this.updateFeatureIdeaItem(this.selectedItem);
      }

      return this.updateItem(this.selectedItem, {
        description: this.modalDraft.description,
        status: this.modalDraft.status,
        title: this.modalDraft.title || null,
        type: this.modalDraft.type,
      });
    },
    sourceLabel(sourceType) {
      if (sourceType === 'feature_ideas') {
        return 'Feature Idea';
      }

      if (sourceType === 'bug_reports') {
        return 'Feedback';
      }

      return sourceType || 'Unknown';
    },
    metaValue(value) {
      if (value === null || value === undefined) {
        return null;
      }

      if (typeof value === 'object') {
        return JSON.stringify(value);
      }

      return String(value);
    },
    moveItemToColumn(item, column) {
      if (!item || item.status === column.key) {
        return;
      }

      const previousStatus = item.status;

      this.items = this.items.map(candidate => candidate.id === item.id
        ? {
          ...candidate,
          status: column.key,
        }
        : candidate);

      this.adminUpdateProductFeedback({
        id: item.id,
        status: column.key,
      }).then((response) => {
        const updated = response.data.data;
        this.items = this.items.map(candidate => candidate.id === updated.id ? updated : candidate);
        window.app.snackbar(`${this.cardTitle(updated)} moved to ${this.statusLabel(updated.status, updated.type)}`);
      }).catch((error) => {
        this.items = this.items.map(candidate => candidate.id === item.id
          ? {
            ...candidate,
            status: previousStatus,
          }
          : candidate);
        window.app.snackbarError(error);
      });
    },
    statusLabel(status, type = this.activeType) {
      return this.statusOptionsFor(type).find(item => item.key === status)?.label || status;
    },
    statusOptionsFor(type) {
      return boardConfigs[type]?.statuses || statuses;
    },
    technicalEntries(item) {
      const metadata = item.metadata || {};
      const entries = [];

      this.addMeta(entries, 'URL', metadata.current_url, true);
      this.addMeta(entries, 'PostHog', metadata.posthog_distinct_id);
      this.addMeta(entries, 'Session Replay', metadata.posthog_session_replay_url, true);
      this.addMeta(entries, 'Steps', metadata.steps_to_reproduce, false, true);
      this.addMeta(entries, 'Agent', metadata.user_agent, false, true);

      return entries;
    },
    ticketDetailEntries(item) {
      return [
        {
          label: 'Creator',
          value: this.creatorSummary(item),
        },
        {
          label: 'Source',
          value: `${this.sourceLabel(item.source_type)} #${item.source_id}`,
        },
        {
          label: 'Submitted',
          value: this.formatDate(item.submitted_at || item.created_at),
        },
      ];
    },
    typeClass(type) {
      if (type === 'bug') {
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      }

      if (type === 'feature_idea') {
        return 'bg-brand-primary-100 text-brand-primary-800 dark:bg-brand-primary-900 dark:text-brand-primary-100';
      }

      return 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200';
    },
    typeCount(type) {
      if (type === 'all') {
        return this.items.length;
      }

      return this.items.filter(item => item.type === type).length;
    },
    typeLabel(type) {
      return types.find(item => item.key === type)?.label || type;
    },
    updateItem(item, payload) {
      this.errors = {
        ...this.errors,
        [item.id]: null,
      };
      this.updatingId = item.id;

      return this.adminUpdateProductFeedback({
        id: item.id,
        ...payload,
      }).then((response) => {
        const updated = response.data.data;
        this.items = this.items.map(candidate => candidate.id === updated.id ? updated : candidate);
        this.modalDraft = {
          description: updated.description || '',
          status: updated.status,
          title: updated.title || '',
          type: updated.type,
        };
        window.app.snackbar('Feedback item saved');
      }).catch((error) => {
        this.errors = {
          ...this.errors,
          [item.id]: error.response?.data?.errors?.[0]?.detail || error.response?.data?.message || 'Could not update this item.',
        };
      }).finally(() => {
        this.updatingId = null;
      });
    },
    updateFeatureIdeaItem(item) {
      this.errors = {
        ...this.errors,
        [item.id]: null,
      };
      this.updatingId = item.id;

      return this.adminUpdateFeatureIdea({
        id: item.source_id,
        category: this.modalDraft.category || null,
        description: this.modalDraft.description,
        status: this.featureIdeaStatusForBoardStatus(this.modalDraft.status),
        title: this.modalDraft.title || null,
      }).then(() => this.adminUpdateProductFeedback({
        id: item.id,
        status: this.modalDraft.status,
      })).then(() => {
        window.app.snackbar('Idea saved');

        return this.loadItems().then(() => {
          const updated = this.items.find(candidate => candidate.id === item.id);

          if (updated) {
            this.openItem(updated);
          }
        });
      }).catch((error) => {
        this.errors = {
          ...this.errors,
          [item.id]: error.response?.data?.errors?.[0]?.detail || error.response?.data?.message || 'Could not update this idea.',
        };
      }).finally(() => {
        this.updatingId = null;
      });
    },
    voteDetailSummary(metadata) {
      if (metadata.votes_count === undefined) {
        return 'No votes yet';
      }

      return `${metadata.votes_count || 0} total, ${metadata.up_votes_count || 0} up, ${metadata.down_votes_count || 0} down`;
    },
    voteSummary(metadata) {
      if (metadata.votes_count === undefined) {
        return null;
      }

      return `+${metadata.up_votes_count || 0} / -${metadata.down_votes_count || 0}`;
    },
    featureIdeaStatusForBoardStatus(status) {
      if (status === 'in_progress') {
        return 'accepted';
      }

      if (status === 'done') {
        return 'shipped';
      }

      return 'new';
    },
  },
});
</script>

<style scoped>
.feedback-modal-layout {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.feedback-modal-editor,
.feedback-modal-meta {
  min-width: 0;
}

@media (min-width: 1024px) {
  .feedback-modal-layout {
    flex-direction: row;
  }

  .feedback-modal-editor {
    flex: 1 1 auto;
  }

  .feedback-modal-meta {
    flex: 0 0 20rem;
    width: 20rem;
  }
}

.ticket-card-title,
.ticket-card-description {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ticket-card-title {
  -webkit-line-clamp: 2;
}

.ticket-card-description {
  -webkit-line-clamp: 2;
}
</style>
